import Form, { Rule } from 'antd/lib/form'
import Space from 'antd/lib/space'
import { AttributesFactory } from 'Core/AttributesFactory'
import { AttributeValueType, FileValueType } from 'Types/Attribute.types'
import { InputType, TaskAttribute } from 'Types/Tasks.types'
import styles from './Attributes.module.less'
import { useMemo } from 'react'
import { AttributeDescription } from 'Components/tasks/Attributes/AttributeDescription'

type AttributesContentPropsType = {
    attribute: TaskAttribute
    value: AttributeValueType
    onUpdateAttribute: (name: string, value: AttributeValueType) => void
    isReadOnly?: () => boolean
    taskId?: string
}

export const AttributesContent = (props: AttributesContentPropsType) => {
    const attributeProps = {
        value: props.value,
        config: props.attribute,
        className: styles['attribute'],
        onChange: (value: AttributeValueType, name: string) =>
            props.onUpdateAttribute?.(name, value),
        readonly: props.isReadOnly?.(),
        taskId: props.taskId,
    }
    const rules: Rule[] = []

    if (props.attribute.input_definition?.input_type === InputType.FILE) {
        rules.push({
            validator: (_, value: FileValueType | FileValueType[]) => {
                const file = Array.isArray(value) ? value[0] : value

                if (!file || !file.download_url) {
                    return Promise.reject(new Error('Please upload a file'))
                }
                return Promise.resolve()
            },
        })
    } else {
        if (!props.attribute.input_definition?.is_optional) {
            rules.push({
                validator: (_, value) => {
                    if (!value || (Array.isArray(value) && value.length === 0)) {
                        return Promise.reject(new Error('Please fill out this field.'))
                    }
                    return Promise.resolve()
                },
            })
        }
        if (props.attribute.input_definition?.input_type === InputType.INT_SCALE) {
            rules.push({
                type: 'number',
                min: props.attribute.input_definition.min,
                max: props.attribute.input_definition.max,
                message: `Value should be between ${props.attribute.input_definition.min} and ${props.attribute.input_definition.max}`,
            })
        }

        if (props.attribute.input_definition?.input_type === InputType.INT) {
            rules.push({
                validator: (_, value) => {
                    if (value && !Number.isInteger(value)) {
                        return Promise.reject(new Error('Only integer values are allowed'))
                    }
                    return Promise.resolve()
                },
                required: true,
            })
        }
    }

    const label = useMemo(() => {
        return (
            <div>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: '13px',
                    }}
                >
                    <div>{props.attribute.label}</div>
                    <Space style={{ marginLeft: 3 }}>
                        {!props.attribute.input_definition?.is_optional && (
                            <span className={styles['label-error']}>*</span>
                        )}
                    </Space>
                </div>
                <AttributeDescription description={props.attribute.description} />
            </div>
        )
    }, [
        props.attribute.description,
        props.attribute.input_definition?.is_optional,
        props.attribute.label,
    ])

    return (
        <Form.Item
            name={props.attribute.name}
            rules={rules}
            key={props.attribute.label}
            label={label}
            labelCol={{ style: { order: 0 } }}
            colon={false}
        >
            <AttributesFactory key={props.attribute.attribute_id} {...attributeProps} />
        </Form.Item>
    )
}
